import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Permissions, ApiPermission, ApiPermissionsResponse } from '@/types/permissions';
import { USER_DATA } from '@/constants/constants';
import { clearUserTypeFromSession } from '@/utils/sessionStorageUtils';

// Auth state interface
interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any;
  token: string | null;
  role: string | null;
  issuperUser: boolean;
  permissions: Permissions[];
  permissionObjects: ApiPermissionsResponse; // Store full permission objects
}

// Initial state
const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: {},
  token: null,
  role: null,
  issuperUser: false,
  permissions: [],
  permissionObjects: {},
};

// Login payload interface
interface LoginPayload {
  user: any;
  token: string;
  role?: string;
  permissions?: Permissions[];
  permissionObjects?: ApiPermissionsResponse;
  issuperUser?: boolean;
  feature_name?: string;
}

// Auth slice
export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    
    // Login action
    loginSuccess: (state, action: PayloadAction<LoginPayload>) => {
      
      state.isAuthenticated = true;
      state.isLoading = false;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.role = action.payload.role || null;
      state.permissions = action.payload.permissions || [];
      state.permissionObjects = action.payload.permissionObjects || {};
      state.issuperUser = action.payload.issuperUser || false;
     
      // Note: sessionStorage is managed by auth service layer
      // Redux state is the source of truth, sessionStorage is synced separately
    },
    
    // Logout action
    logout: (state) => {
      state.isAuthenticated = false;
      state.isLoading = false;
      state.user = {};
      state.token = null;
      state.role = null;
      state.permissions = [];
      state.permissionObjects = {};
      state.issuperUser = false;
      
      // Clear ONLY sessionStorage for this tab (do not touch localStorage - other tabs might use it)
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(USER_DATA);
        clearUserTypeFromSession(); // Clear userType from sessionStorage (this tab only)
      }
    },
    
    // Update user info
    updateUser: (state, action: PayloadAction<Partial<AuthState['user']>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        
        // Note: sessionStorage sync handled by auth service layer
      }
    },
    
    // Update role
    updateRole: (state, action: PayloadAction<string>) => {
      state.role = action.payload;
      
      // Note: sessionStorage sync handled by auth service layer
    },
    
    // Update permissions
    updatePermissions: (state, action: PayloadAction<Permissions[]>) => {
      state.permissions = action.payload;
      
      // Note: sessionStorage sync handled by auth service layer
    },

    
    // Initialize auth state (for app startup)
    initializeAuth: (state, action: PayloadAction<{ isAuthenticated: boolean; user?: AuthState['user']; token?: string; role?: string; permissions?: Permissions[]; permissionObjects?: ApiPermissionsResponse }>) => {
      state.isAuthenticated = action.payload.isAuthenticated;
      state.isLoading = false;
      state.user = action.payload.user || {};
      state.token = action.payload.token || null;
      state.role = action.payload.role || null;
      state.permissions = action.payload.permissions || [];
      state.permissionObjects = action.payload.permissionObjects || {};
      state.issuperUser = action.payload.user?.is_superuser || false;
    },
  },
});

// Export actions
export const {
  setLoading,
  loginSuccess,
  logout,
  updateUser,
  updateRole,
  updatePermissions,
  initializeAuth,
} = authSlice.actions;

// Export reducer
export default authSlice.reducer; 